import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import api from "./apiClient";

import cookiesUtility from "../utils/cookies.utility";

import { HMIDeleteBanner, HMIDeleteBannerImage, HMIUpdateBanner, HMIUploadBannerImage, HMICreateBanner, IBannerListItem } from "../interfaces/banners";

import useToast from "./useToast";

export const useGetBanners = () => {
    const queryFn = async (): Promise<IBannerListItem[]> => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.get("/banners/list/immersion/NO_SCHOOL_REQUIRED");
        return response.data;
    };

    return useQuery({
        queryKey: ["banners"],
        queryFn,
        retry: 0,
    });
};

export const useGetBannerDetails = (id: number | null) => {
    const queryFn = async (): Promise<IBannerListItem> => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.get(`/banners/details/${id}`);
        return response.data;
    };

    return useQuery({
        queryKey: ["banner-details", id],
        queryFn,
        retry: 0,
        enabled: !!id,
    });
};

export const useCreateBanner = () => {
    const { addToast } = useToast();
    const navigate = useNavigate();

    const mutationFn = async (data: HMICreateBanner) => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.post("/banners", data.payload, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsSaving(true);
        },
        onSuccess: (data, variables) => {
            addToast({
                type: 'success',
                title: 'Banner created successfully.',
            });

            variables.setImageFile(null);

            navigate(`/banners/details/${data.id}`);
        },
        onError: (error: any) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });
        },
        onSettled: (_, __, variables) => {
            variables.setIsSaving(false);
        },
    });
};

export const useUpdateBanner = () => {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    const mutationFn = async (data: HMIUpdateBanner) => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.patch(`/banners/${data.id}`, data.payload);
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsSaving(true);
        },
        onSuccess: (_, variables) => {
            addToast({
                type: 'success',
                title: 'Banner updated successfully.',
            });

            queryClient.invalidateQueries({ queryKey: ["banner-details"] });
            variables.setImageFile(null);
        },
        onError: (error: any) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });
        },
        onSettled: (_, __, variables) => {
            variables.setIsSaving(false);
        },
    });
};

export const useDeleteBanner = () => {
    const { addToast } = useToast();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIDeleteBanner) => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.delete(`/banners/${data.id}`);
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsDeleting(true);
        },
        onSuccess: () => {
            addToast({
                type: 'success',
                title: 'Banner deleted successfully.',
            });

            queryClient.invalidateQueries({ queryKey: ["banners"] });

            navigate('/banners');
        },
        onError: (error: any) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });
        },
        onSettled: (_, __, variables) => {
            variables.setIsDeleting(false);
        },
    });
};

export const useUploadBannerImage = () => {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    const mutationFn = async (data: HMIUploadBannerImage) => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.patch(`/banners/upload-image/${data.id}`,
            { file: data.file },
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsImageUploading(true);
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["banner-details"] });
            variables.setImageFile(null);
        },
        onError: (error: any, variables) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });

            variables.setImageFile(null);
        },
        onSettled: (_, __, variables) => {
            variables.setIsImageUploading(false);
        },
    });
};

export const useDeleteBannerImage = () => {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    const mutationFn = async (data: HMIDeleteBannerImage) => {
        const token = cookiesUtility.getCookie(process.env.REACT_APP_AUTH_TOKEN_NAME as string);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const response = await api.delete(`/banners/image/${data.id}`);
        return response.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsImageDeleting(true);
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["banner-details"] });
            variables.setImageFile(null);
        },
        onError: (error: any) => {
            addToast({
                type: 'error',
                title: error.response.data.message,
            });
        },
        onSettled: (_, __, variables) => {
            variables.setIsImageDeleting(false);
        },
    });
};